import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { housesApi, type HouseInput } from '../features/houses/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { Pagination } from '../components/Pagination.tsx';
import { dv } from '../i18n/dv.ts';

const PAGE_SIZE = 50;

export function Houses(): JSX.Element {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!user && (user.isOrgAdmin || user.role === 'ADMIN');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['houses', page, debounced],
    queryFn: () => housesApi.list({ page, pageSize: PAGE_SIZE, search: debounced || undefined }),
  });

  const houses = data?.houses ?? [];
  const total = data?.total ?? 0;

  // Add-house dialog
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<HouseInput & { numberText: string }>({ name: '', numberText: '', ward: '', address: '' });
  const [addError, setAddError] = useState(false);
  const create = useMutation({
    mutationFn: () => housesApi.create({
      name: form.name.trim(),
      number: form.numberText.trim() ? Number(form.numberText) : undefined,
      ward: form.ward?.trim() || undefined,
      address: form.address?.trim() || undefined,
    }),
    onSuccess: () => {
      setShowAdd(false);
      setForm({ name: '', numberText: '', ward: '', address: '' });
      setAddError(false);
      void qc.invalidateQueries({ queryKey: ['houses'] });
    },
    onError: () => setAddError(true),
  });

  // Bulk loader
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkError, setBulkError] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ created: number; skipped: number } | null>(null);
  const bulk = useMutation({
    mutationFn: () => {
      const names = bulkText.split('\n').map((l) => l.trim()).filter(Boolean);
      return housesApi.bulk(names.map((name) => ({ name })));
    },
    onSuccess: (r) => {
      setBulkResult(r);
      setBulkText('');
      setBulkError(false);
      void qc.invalidateQueries({ queryKey: ['houses'] });
    },
    onError: () => setBulkError(true),
  });

  return (
    <div className="page space-y-5 md:space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-3xl leading-tight tracking-tight md:text-[2.5rem]">{dv.houses.title}</h1>
          {total > 0 && <span className="num tabular text-sm text-muted-foreground">{total}</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/family" className="btn-ghost">
            <Icon name="family_history" size={18} /> Family tree
          </Link>
        {isAdmin && (
          <>
            <button
              onClick={() => { setShowBulk(true); setBulkResult(null); setBulkError(false); }}
              className="btn-secondary"
            >
              <Icon name="upload" size={18} /> {dv.houses.bulkLoad}
            </button>
            <button
              onClick={() => { setShowAdd(true); setAddError(false); }}
              className="btn-primary"
            >
              <Icon name="add" size={18} /> {dv.houses.add}
            </button>
          </>
        )}
        </div>
      </header>

      {/* Search sits on the chrome ground as its own toolbar, the way the other
          list surfaces in the app do, instead of floating as a bare input. */}
      <div className="rounded-full bg-[hsl(var(--chrome))] p-2 sm:px-3">
        <div className="relative w-full sm:max-w-md">
          <Icon
            name="search"
            size={18}
            className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            className="w-full rounded-full bg-[hsl(var(--background))] py-2 pe-4 ps-11 text-sm outline-none transition focus:ring-2 focus:ring-ring/30"
            value={search}
            placeholder={dv.houses.search}
            aria-label={dv.houses.search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* One list at every width — the page used to render the same data twice,
          a card <ul> for mobile and a separate <table> for desktop, which had to
          be kept in step by hand. The address column drops below `sm`. */}
      {isLoading ? (
        <p className="status-label" role="status" aria-live="polite">{dv.common.loading}</p>
      ) : houses.length === 0 ? (
        <div className="card-paper space-y-3 p-10 text-center">
          <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
            <Icon name="home" size={22} />
          </div>
          <p className="text-sm text-muted-foreground">{dv.houses.empty}</p>
          {isAdmin && (
            <button onClick={() => { setShowAdd(true); setAddError(false); }} className="btn-secondary">
              {dv.houses.add}
            </button>
          )}
        </div>
      ) : (
        <>
          <ul className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))]">
            {houses.map((h) => (
              <li key={h.id} className="border-b border-black/[0.05] last:border-b-0 dark:border-white/[0.06]">
                <Link
                  to="/houses/$houseId"
                  params={{ houseId: h.id }}
                  className="flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
                >
                  <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                    <Icon name="home" size={22} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[0.95rem] font-medium text-ink">{h.name}</div>
                    <div className="truncate text-sm text-muted-foreground">
                      {[h.number != null ? `${dv.houses.numberShort} ${h.number}` : null, h.ward, h.address].filter(Boolean).join(' · ') || dv.common.none}
                    </div>
                  </div>
                  <Icon name="chevron_right" size={18} className="shrink-0 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>

          <Pagination
            page={data?.page ?? page}
            pageSize={data?.pageSize ?? PAGE_SIZE}
            total={total}
            onChange={setPage}
          />
        </>
      )}

      {isAdmin && (
        <Dialog open={showAdd} onClose={() => setShowAdd(false)} title={dv.houses.add}>
          <form
            className="space-y-4"
            onSubmit={(e) => { e.preventDefault(); setAddError(false); create.mutate(); }}
          >
            <div className="space-y-1.5">
              <label className="field-label" htmlFor="house-name">{dv.houses.name}</label>
              <input
                id="house-name"
                required
                autoFocus
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <label className="field-label" htmlFor="house-number">{dv.houses.number}</label>
                <input
                  id="house-number"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  dir="ltr"
                  className="input"
                  value={form.numberText}
                  onChange={(e) => setForm((f) => ({ ...f, numberText: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <label className="field-label" htmlFor="house-ward">{dv.houses.ward}</label>
                <input
                  id="house-ward"
                  className="input"
                  value={form.ward}
                  onChange={(e) => setForm((f) => ({ ...f, ward: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <label className="field-label" htmlFor="house-address">{dv.houses.address}</label>
                <input
                  id="house-address"
                  className="input"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                />
              </div>
            </div>
            {addError && <p className="text-sm text-destructive" role="alert">{dv.common.error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost">
                {dv.common.cancel}
              </button>
              <button type="submit" disabled={create.isPending} className="btn-primary">
                {create.isPending ? dv.common.saving : dv.common.save}
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog open={showBulk} onClose={() => setShowBulk(false)} title={dv.houses.bulkLoad}>
          <form
            className="space-y-4"
            onSubmit={(e) => { e.preventDefault(); setBulkResult(null); setBulkError(false); bulk.mutate(); }}
          >
            <p className="text-sm text-muted-foreground">{dv.houses.bulkHint}</p>
            <textarea
              className="input min-h-[11rem]"
              dir="auto"
              autoFocus
              value={bulkText}
              placeholder={dv.houses.bulkPlaceholder}
              aria-label={dv.houses.bulkLoad}
              onChange={(e) => setBulkText(e.target.value)}
            />
            {bulkError && <p className="text-sm text-destructive" role="alert">{dv.common.error}</p>}
            {bulkResult && (
              <p className="text-sm text-[hsl(var(--success))]" role="status">
                {dv.houses.bulkResult(bulkResult.created, bulkResult.skipped)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowBulk(false)} className="btn-ghost">
                {dv.common.cancel}
              </button>
              <button
                type="submit"
                disabled={bulk.isPending || bulkText.trim().length === 0}
                className="btn-primary"
              >
                {bulk.isPending ? dv.common.saving : dv.houses.bulkSubmit}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </div>
  );
}
